import type { ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import type { BookingResponseDTO } from "../../../types/dtos/api/purchase/purchase-booking.dto";
import type { PurchaseCheckoutModeDTO, PurchasePaymentStatusDTO } from "../../../types/dtos/api/purchase/purchase-payment.dto";
import type { PrebookResponseDTO } from "../../../types/dtos/api/purchase/purchase-prebook.dto";
import { defineEphemeralSlice } from "../runtime/defineEphemeralSlice";

export type TicketPurchaseStep = "selection" | "summary" | "payment" | "confirmation";

/**
 * Payment progress as seen by the wizard.
 *
 * The local phases (`creating`, `awaiting_return`, `verifying`) are UI-only;
 * the authoritative value is the `PurchasePaymentStatusDTO` returned by the
 * backend after its Payrexx API re-check.
 */
export type TicketPurchasePaymentStatus =
  "idle" |
  "creating" |
  "awaiting_return" |
  "verifying" |
  PurchasePaymentStatusDTO;

/**
 * Booking progress as seen by the wizard.
 *
 * - `verifying` — the backend answered `BOOKING_IN_PROGRESS` (the booking request is still
 *   running); the wizard polls the attempt until it is booked or failed.
 * - `unconfirmed` — inconclusive outcome: the poll budget expired while the attempt was
 *   still `booking_in_progress` server-side. The backend may still complete (the delivered
 *   tickets then appear in « Mes billets ») or compensate the payment — the wizard never
 *   restarts the purchase on its own from here (a late success would double-book it).
 * - `failed` — definitive failure: the attempt is closed, tickets are never delivered
 *   (including `booking_unresolved`, which the backend cancels or refunds), the payment
 *   is released or refunded, and the shopper starts a new purchase.
 */
export type TicketPurchaseBookingStatus = "idle" | "loading" | "verifying" | "succeeded" | "unconfirmed" | "failed";

export type TicketPurchaseClass = "1" | "2";

export interface SelectedPurchaseOffer {
    "segmentId":       string;
    "bundleIndex":     number;
    "offerIndex":      number;
    "offerIdentifier": string;
}

export interface TicketPurchaseState {
    "isOpen":                boolean;
    "itinerary":             ItineraryDTO | null;
    "step":                  TicketPurchaseStep;
    "selectionSegmentIndex": number;
    "selectedOffers":        Record<string, SelectedPurchaseOffer>;
    "segmentClass":          Record<string, TicketPurchaseClass>;
    "skipSegment":           Record<string, boolean>;
    "prebookingResponse":    PrebookResponseDTO | null;

    /** Purchase attempt id — kept separately so resume works even without a prebooking response. */
    "purchaseAttemptId": string | null;

    /** FlowRide payment id of the current Payrexx gateway, `null` before gateway creation. */
    "paymentId": string | null;

    /** Payrexx hosted checkout URL while a gateway is open (kept for diagnostics/return recovery). */
    "payrexxUrl": string | null;

    /** Checkout mode chosen on the order summary (« Payer » = `standard` hosted page, « Paiement en un clic » = `direct`). */
    "checkoutMode": PurchaseCheckoutModeDTO;

    /**
     * Whether the shopper picked the checkout mode on the summary selector.
     *
     * `false` while the mode only carries its default — the saved-payment-method
     * lookup (`GET /api/purchase/payment/method`) may still replace it with
     * `direct` (an existing saved method is the one-click opt-in signal).
     * A selector click or a Payrexx resume sets it.
     */
    "checkoutModeTouched": boolean;
    "paymentStatus":       TicketPurchasePaymentStatus;
    "bookingStatus":       TicketPurchaseBookingStatus;
    "booking":             BookingResponseDTO | null;
    "error":               string | null;

    /** Informational message shown on the selection step (e.g. offers refreshed after a price change). */
    "notice":   string | null;
    "canClose": boolean;
}

export const TICKET_PURCHASE_DEFAULT: TicketPurchaseState = {
    "isOpen":                false,
    "itinerary":             null,
    "step":                  "selection",
    "selectionSegmentIndex": 0,
    "selectedOffers":        {},
    "segmentClass":          {},
    "skipSegment":           {},
    "prebookingResponse":    null,
    "purchaseAttemptId":     null,
    "paymentId":             null,
    "payrexxUrl":            null,
    "checkoutMode":          "standard",
    "checkoutModeTouched":   false,
    "paymentStatus":         "idle",
    "bookingStatus":         "idle",
    "booking":               null,
    "error":                 null,
    "notice":                null,
    "canClose":              true,
};

export const ticketPurchaseSlice = defineEphemeralSlice<TicketPurchaseState>( {
    "key":     "ticketPurchase",
    "default": () => TICKET_PURCHASE_DEFAULT,
} );
